import { logger } from '@/utils/logger'
import { getAllOrders, getDesignOrders, getOperationsOrders } from '../data/orders'
import { getRegionFromState, REGIONS } from '../data/regions'

const log = logger.module('LeadTimeCalculator')

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export interface DeliveryEstimate {
  weeks?: string
  weeksMin?: number
  weeksMax?: number
  text?: string
  estimatedDate?: string
}

export interface TimelinePhase {
  design: { days: number; completeBy: Date }
  fabrication: { days: number; completeBy: Date }
  delivery: { days: number; deliverBy: Date; installBy: Date }
}

export interface LeadTimePrediction {
  totalDays: number
  estimatedDelivery: Date
  estimatedInstall: Date
  confidence: {
    earliest: Date
    latest: Date
    varianceDays: number
  }
  timeline: Record<string, number>
  phases: TimelinePhase
  context: {
    config: string
    regionId: string
    regionName: string
    designQueuePosition: number
    historicalSampleSize: number
  }
  formatted: {
    delivery: string
    install: string
    range: string
    weeksOut: number
  }
}

export interface HistoricalMetrics {
  avgDesignDays: number
  avgFabricationDays: number
  avgTotalDays?: number
  avgShippingDays?: number
  sampleSize: number
  lastCalculated?: string
}

/**
 * Configuration - Default Durations (in days)
 */
const DEFAULT_DURATIONS = {
  // Design phase
  designQueueWaitPerOrder: 2,
  designMeetingScheduling: 3,
  designDuration: 5,

  // Fabrication phase
  fabricationQueueWait: 2,
  fabricationByConfig: {
    'Twin/Twin': 7,
    'Twin/Full': 8,
    'Twin/Queen': 9,
    'Full/Full': 9,
    'Full/Queen': 10,
    'Queen/Queen': 10,
    'Triple': 12,
    'Quad': 14,
    'L-Shape': 12,
    'Staircase': 10,
    'Custom': 14,
    'default': 10
  } as Record<string, number>,

  // QC and packing
  qcDuration: 2,
  packingDuration: 1,

  // Shipping by region (from central US)
  shippingByRegion: {
    'new-england': 5,
    'mid-atlantic': 4,
    'southeast': 3,
    'midwest': 2,
    'great-plains': 2,
    'southwest': 3,
    'mountain': 3,
    'pacific-northwest': 4,
    'california': 4,
    'unknown': 4
  } as Record<string, number>,

  // Installation
  installBuffer: 3,
  installDuration: 1
}

const METRICS_KEY = 'bliss_lead_time_metrics'

/**
 * Get historical metrics from completed orders
 */
// In-memory metrics cache
let metricsCache: HistoricalMetrics | null = null

function getHistoricalMetrics(): HistoricalMetrics {
  if (metricsCache) {
    return metricsCache
  }
  // Calculate from completed orders
  return calculateHistoricalMetrics()
}

/**
 * Calculate historical averages from completed orders
 */
function calculateHistoricalMetrics(): HistoricalMetrics {
  const orders = getAllOrders()
  const completed = orders.filter((o: any) =>
    o.fabricationStatus === 'complete' ||
    o.pipeline?.complete ||
    o.status === 'completed'
  )

  if (completed.length < 3) {
    // Not enough data, use defaults
    return {
      avgDesignDays: DEFAULT_DURATIONS.designDuration,
      avgFabricationDays: DEFAULT_DURATIONS.fabricationByConfig.default,
      avgTotalDays: 21,
      avgShippingDays: 3,
      sampleSize: 0
    }
  }

  // Calculate averages
  const designTimes: number[] = []
  const fabTimes: number[] = []
  const totalTimes: number[] = []

  completed.forEach((order: any) => {
    // Design time
    if (order.payment?.depositPaidDate && order.pipeline?.designApprovedDate) {
      const start = new Date(order.payment.depositPaidDate)
      const end = new Date(order.pipeline.designApprovedDate)
      const days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24))
      if (days > 0 && days < 60) designTimes.push(days)
    }

    // Fabrication time
    if (order.claimedByPartnerAt && order.fabricationCompletedAt) {
      const start = new Date(order.claimedByPartnerAt)
      const end = new Date(order.fabricationCompletedAt)
      const days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24))
      if (days > 0 && days < 60) fabTimes.push(days)
    }

    // Total time
    if (order.createdAt && (order.pipeline?.completeDate || order.fabricationCompletedAt)) {
      const start = new Date(order.createdAt)
      const end = new Date(order.pipeline?.completeDate || order.fabricationCompletedAt)
      const days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24))
      if (days > 0 && days < 120) totalTimes.push(days)
    }
  })

  const avg = (arr: number[]) => arr.length > 0 ? Math.round(arr.reduce((a, b) => a + b, 0) / arr.length) : null

  const metrics: HistoricalMetrics = {
    avgDesignDays: avg(designTimes) || DEFAULT_DURATIONS.designDuration,
    avgFabricationDays: avg(fabTimes) || DEFAULT_DURATIONS.fabricationByConfig.default,
    avgTotalDays: avg(totalTimes) || 21,
    avgShippingDays: 3,
    sampleSize: completed.length,
    lastCalculated: new Date().toISOString()
  }

  // Cache in memory
  metricsCache = metrics

  return metrics
}

/**
 * Calculate predicted delivery date for an order
 */
export function predictLeadTime(order: any, options: { useHistorical?: boolean } = {}): LeadTimePrediction {
  const now = new Date()
  const metrics = getHistoricalMetrics()

  // Get configuration complexity
  const config = order.bedConfig || order.config || order.beds?.[0]?.configId || 'default'
  const fabDuration = DEFAULT_DURATIONS.fabricationByConfig[config] ||
    DEFAULT_DURATIONS.fabricationByConfig.default

  // Get region for shipping
  const state = order.client?.state || ''
  const regionId = getRegionFromState(state)
  const shippingDays = DEFAULT_DURATIONS.shippingByRegion[regionId] ||
    DEFAULT_DURATIONS.shippingByRegion.unknown

  // Calculate queue positions
  const designQueue = getDesignOrders()
  const designQueuePosition = designQueue.length

  // Build the timeline
  const timeline = {
    // Phase 1: Design
    designQueueWait: designQueuePosition * DEFAULT_DURATIONS.designQueueWaitPerOrder,
    designScheduling: DEFAULT_DURATIONS.designMeetingScheduling,
    designWork: options.useHistorical ? metrics.avgDesignDays : DEFAULT_DURATIONS.designDuration,

    // Phase 2: Fabrication
    fabQueueWait: DEFAULT_DURATIONS.fabricationQueueWait,
    fabrication: options.useHistorical ? metrics.avgFabricationDays : fabDuration,
    qc: DEFAULT_DURATIONS.qcDuration,
    packing: DEFAULT_DURATIONS.packingDuration,

    // Phase 3: Delivery
    shipping: shippingDays,
    installBuffer: DEFAULT_DURATIONS.installBuffer,
    install: DEFAULT_DURATIONS.installDuration
  }

  // Calculate totals
  const designTotal = timeline.designQueueWait + timeline.designScheduling + timeline.designWork
  const fabTotal = timeline.fabQueueWait + timeline.fabrication + timeline.qc + timeline.packing
  const deliveryTotal = timeline.shipping + timeline.installBuffer + timeline.install
  const totalDays = designTotal + fabTotal + deliveryTotal

  // Calculate dates
  const startDate = order.payment?.depositPaidDate ? new Date(order.payment.depositPaidDate) : now
  const designCompleteDate = addBusinessDays(startDate, designTotal)
  const fabCompleteDate = addBusinessDays(designCompleteDate, fabTotal)
  const deliveryDate = addBusinessDays(fabCompleteDate, timeline.shipping)
  const installDate = addBusinessDays(deliveryDate, timeline.installBuffer + timeline.install)

  // Calculate confidence range
  const varianceDays = Math.ceil(totalDays * 0.15)
  const earliestDate = addBusinessDays(startDate, totalDays - varianceDays)
  const latestDate = addBusinessDays(startDate, totalDays + varianceDays)

  return {
    totalDays,
    estimatedDelivery: deliveryDate,
    estimatedInstall: installDate,

    confidence: {
      earliest: earliestDate,
      latest: latestDate,
      varianceDays
    },

    timeline,
    phases: {
      design: {
        days: designTotal,
        completeBy: designCompleteDate
      },
      fabrication: {
        days: fabTotal,
        completeBy: fabCompleteDate
      },
      delivery: {
        days: deliveryTotal,
        deliverBy: deliveryDate,
        installBy: installDate
      }
    },

    context: {
      config,
      regionId,
      regionName: REGIONS[regionId]?.name || 'Unknown',
      designQueuePosition,
      historicalSampleSize: metrics.sampleSize
    },

    formatted: {
      delivery: formatDate(deliveryDate),
      install: formatDate(installDate),
      range: `${formatDate(earliestDate)} - ${formatDate(latestDate)}`,
      weeksOut: Math.ceil(totalDays / 7)
    }
  }
}

/**
 * Get a quick estimate for display (simpler calculation)
 */
export function getQuickEstimate(order: any): { weeks: number; range: string; deliveryDate: string } {
  const prediction = predictLeadTime(order, { useHistorical: true })

  return {
    weeks: prediction.formatted.weeksOut,
    range: `${prediction.confidence.earliest.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${prediction.confidence.latest.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
    deliveryDate: prediction.formatted.delivery
  }
}

/**
 * Get estimated delivery for quote display
 */
export function getQuoteDeliveryEstimate(config?: string, state?: string): DeliveryEstimate {
  const mockOrder = {
    bedConfig: config,
    client: { state }
  }

  const prediction = predictLeadTime(mockOrder)

  return {
    weeksMin: Math.max(2, prediction.formatted.weeksOut - 1),
    weeksMax: prediction.formatted.weeksOut + 1,
    text: `${Math.max(2, prediction.formatted.weeksOut - 1)}-${prediction.formatted.weeksOut + 1} weeks`,
    estimatedDate: prediction.formatted.delivery
  }
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Add business days to a date (skip weekends)
 */
function addBusinessDays(startDate: Date, days: number): Date {
  const date = new Date(startDate)
  let remaining = days

  while (remaining > 0) {
    date.setDate(date.getDate() + 1)
    const dayOfWeek = date.getDay()
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      remaining--
    }
  }

  return date
}

/**
 * Format date for display
 */
function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

/**
 * Get remaining days until a target date
 */
export function getDaysUntil(targetDate: string | Date): number {
  const now = new Date()
  const target = new Date(targetDate)
  const diff = Math.ceil((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
  return diff
}

/**
 * Check if order is on track for its due date
 */
export function isOnTrack(order: any): {
  onTrack: boolean
  message: string
  bufferDays?: number
  daysLate?: number
} {
  if (!order.dueDate) return { onTrack: true, message: 'No due date set' }

  const prediction = predictLeadTime(order)
  const dueDate = new Date(order.dueDate)

  if (prediction.estimatedDelivery <= dueDate) {
    const bufferDays = getDaysUntil(dueDate) - prediction.totalDays
    return {
      onTrack: true,
      message: `On track with ${bufferDays} day buffer`,
      bufferDays
    }
  } else {
    const daysLate = Math.ceil((prediction.estimatedDelivery.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24))
    return {
      onTrack: false,
      message: `May be ${daysLate} days late`,
      daysLate
    }
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  predictLeadTime,
  getQuickEstimate,
  getQuoteDeliveryEstimate,
  getDaysUntil,
  isOnTrack,
  DEFAULT_DURATIONS
}
